import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, Loader2, Pencil, Plus, Save, Trash2 } from '@/lib/icons'
import type { BookCreateRequest, BookItem } from '@ayub/shared'
import { formatSom } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { ToastViewport, useToast } from '@/components/ui/toast'
import { useCurrentAdmin } from '@/components/settings/hooks'
import { parseIntegerInput } from '@/components/settings/numericInput'
import { ApiError } from '@/lib/api'
import { createBook, deleteBook, fetchBooks, updateBook } from './api'

/**
 * "Kitoblar" bo'limi — mustaqil kitob katalogi (nom, muallif, narx, izoh). O'qish har
 * adminga ochiq; qo'shish/tahrirlash/o'chirish FAQAT super adminga (server ham
 * `requireSuperAdmin` bilan qayta tekshiradi — bu yerdagi gating birinchi qatlam).
 */
export function BooksPage() {
  const queryClient = useQueryClient()
  const { toast, showToast } = useToast()
  const currentAdmin = useCurrentAdmin()
  const readOnly = !currentAdmin.isSuper

  const booksQuery = useQuery<BookItem[], Error>({ queryKey: ['books'], queryFn: fetchBooks })

  // `null` = oyna yopiq; `'create'` = yangi kitob; `BookItem` = shu kitobni tahrirlash.
  const [formTarget, setFormTarget] = useState<'create' | BookItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BookItem | null>(null)

  function invalidateBooks() {
    void queryClient.invalidateQueries({ queryKey: ['books'] })
  }

  const saveMutation = useMutation<BookItem, Error, { id: number | null; body: BookCreateRequest }>(
    {
      mutationFn: ({ id, body }) => (id === null ? createBook(body) : updateBook(id, body)),
      onSuccess: (_data, variables) => {
        invalidateBooks()
        setFormTarget(null)
        showToast(variables.id === null ? "Kitob qo'shildi" : 'Kitob yangilandi')
      },
    },
  )

  const deleteMutation = useMutation<{ ok: true }, Error, number>({
    mutationFn: (id) => deleteBook(id),
    onSuccess: () => {
      invalidateBooks()
      setDeleteTarget(null)
      showToast("Kitob o'chirildi")
    },
  })

  // Oyna har ochilganda mutatsiya xatosi tozalansin (oldingi urinish qolib ketmasin).
  // `saveMutation` ATAYLAB dependency ro'yxatida yo'q — faqat `formTarget` o'zgarishida
  // (oyna ochilishida) ishlashi kerak (`VideoDetail.tsx`dagi bir xil naqsh).
  useEffect(() => {
    if (formTarget !== null) saveMutation.reset()
  }, [formTarget])

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <BookOpen size={20} aria-hidden="true" /> Kitoblar
        </h1>
        {!readOnly && (
          <Button size="sm" onClick={() => setFormTarget('create')}>
            <Plus size={16} aria-hidden="true" />
            Kitob qo&apos;shish
          </Button>
        )}
      </header>

      <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
        {readOnly && currentAdmin.isKnown && (
          <p className="rounded-card border border-app-border bg-app-card px-3 py-2 text-xs text-app-hint">
            Kitoblarni faqat super admin qo&apos;sha/tahrirlay oladi. Quyidagi ro&apos;yxat faqat
            ko&apos;rish uchun.
          </p>
        )}

        {booksQuery.isLoading ? (
          <BooksSkeleton />
        ) : booksQuery.isError ? (
          <div className="flex flex-col items-start gap-2 rounded-card border border-app-border bg-app-card p-4">
            <p className="text-sm text-stage-rejected-text">
              {booksQuery.error instanceof ApiError
                ? booksQuery.error.message
                : "Ma'lumotni yuklab bo'lmadi. Internetni tekshiring."}
            </p>
            <Button size="sm" variant="secondary" onClick={() => void booksQuery.refetch()}>
              Qayta urinish
            </Button>
          </div>
        ) : booksQuery.data && booksQuery.data.length > 0 ? (
          <ul className="flex flex-col gap-3">
            {booksQuery.data.map((book) => (
              <BookCard
                key={book.id}
                book={book}
                readOnly={readOnly}
                onEdit={() => setFormTarget(book)}
                onDelete={() => setDeleteTarget(book)}
              />
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-card border border-dashed border-app-border bg-app-card px-4 py-10 text-center">
            <BookOpen size={28} className="text-app-hint" aria-hidden="true" />
            <p className="text-sm text-app-hint">Hali kitob qo&apos;shilmagan.</p>
            {!readOnly && (
              <Button size="sm" variant="secondary" onClick={() => setFormTarget('create')}>
                <Plus size={16} aria-hidden="true" />
                Birinchi kitobni qo&apos;shish
              </Button>
            )}
          </div>
        )}
      </div>

      <BookFormModal
        target={formTarget}
        isSubmitting={saveMutation.isPending}
        errorMessage={saveMutation.error ? saveMutation.error.message : null}
        onCancel={() => setFormTarget(null)}
        onSubmit={(id, body) => saveMutation.mutate({ id, body })}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Kitobni o'chirasizmi?"
        description={
          deleteTarget ? (
            <>
              <strong>{deleteTarget.title}</strong> ({deleteTarget.author}) katalogdan butunlay
              o&apos;chiriladi. Bu amalni orqaga qaytarib bo&apos;lmaydi.
            </>
          ) : null
        }
        confirmLabel="Ha, o'chirish"
        tone="danger"
        isLoading={deleteMutation.isPending}
        errorMessage={deleteMutation.error ? deleteMutation.error.message : null}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      <ToastViewport message={toast?.message ?? null} />
    </div>
  )
}

interface BookCardProps {
  book: BookItem
  readOnly: boolean
  onEdit: () => void
  onDelete: () => void
}

function BookCard({ book, readOnly, onEdit, onDelete }: BookCardProps) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-card border border-app-border bg-app-card p-4">
      <div className="min-w-0 flex-1">
        <h2 className="font-semibold text-app-text">{book.title}</h2>
        <p className="text-sm text-app-hint">{book.author}</p>
        {book.description && <p className="mt-1 text-sm text-app-text">{book.description}</p>}
        <p className="tabular mt-2 text-sm font-semibold text-brand">{formatSom(book.price)}</p>
      </div>
      {!readOnly && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            aria-label="Kitobni tahrirlash"
            title="Tahrirlash"
            className="flex size-11 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg hover:text-brand"
          >
            <Pencil size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label="Kitobni o'chirish"
            title="O'chirish"
            className="flex size-11 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg hover:text-stage-rejected-text"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      )}
    </li>
  )
}

interface BookFormModalProps {
  /** `null` = yopiq, `'create'` = yangi, `BookItem` = tahrirlash. */
  target: 'create' | BookItem | null
  isSubmitting: boolean
  errorMessage: string | null
  onCancel: () => void
  onSubmit: (id: number | null, body: BookCreateRequest) => void
}

function BookFormModal({
  target,
  isSubmitting,
  errorMessage,
  onCancel,
  onSubmit,
}: BookFormModalProps) {
  const editing = target !== null && target !== 'create' ? target : null
  const open = target !== null

  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [price, setPrice] = useState('')
  const [description, setDescription] = useState('')
  const [touched, setTouched] = useState(false)

  // Oyna har ochilganda maydonlarni to'ldiradi/tozalaydi (tahrirlashda joriy qiymatlar bilan).
  useEffect(() => {
    if (!open) return
    setTitle(editing?.title ?? '')
    setAuthor(editing?.author ?? '')
    setPrice(editing ? String(editing.price) : '')
    setDescription(editing?.description ?? '')
    setTouched(false)
    // `editing`/`open` ATAYLAB dependency EMAS — ular `target`dan kelib chiqadi, faqat
    // `target` o'zgarganda (oyna ochilishida) maydonlar qayta to'ldirilishi kerak.
  }, [target])

  const parsedPrice = parseIntegerInput(price)
  const titleOk = title.trim().length > 0
  const authorOk = author.trim().length > 0
  const priceOk = parsedPrice !== null && parsedPrice >= 0
  const canSubmit = titleOk && authorOk && priceOk && !isSubmitting

  function handleSubmit() {
    setTouched(true)
    if (!titleOk || !authorOk || parsedPrice === null || parsedPrice < 0) return
    onSubmit(editing?.id ?? null, {
      title: title.trim(),
      author: author.trim(),
      price: parsedPrice,
      description: description.trim().length > 0 ? description.trim() : undefined,
    })
  }

  return (
    <Modal
      open={open}
      onClose={onCancel}
      ariaLabel={editing ? 'Kitobni tahrirlash' : "Kitob qo'shish"}
      size="compact"
      zIndex={50}
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">
          {editing ? 'Kitobni tahrirlash' : "Kitob qo'shish"}
        </h2>

        <Field
          id="book-title"
          label="Kitob nomi"
          value={title}
          onChange={setTitle}
          disabled={isSubmitting}
          error={touched && !titleOk ? 'Kitob nomi kiritilishi shart' : null}
          placeholder="Masalan: Atomic Habits"
        />
        <Field
          id="book-author"
          label="Muallif"
          value={author}
          onChange={setAuthor}
          disabled={isSubmitting}
          error={touched && !authorOk ? 'Muallif kiritilishi shart' : null}
          placeholder="Masalan: James Clear"
        />
        <Field
          id="book-price"
          label="Narx (so'm)"
          value={price}
          onChange={setPrice}
          disabled={isSubmitting}
          inputMode="numeric"
          error={touched && !priceOk ? 'Narxni raqamda kiriting (masalan: 89 000)' : null}
          placeholder="Masalan: 89 000"
        />

        <div className="flex flex-col gap-1">
          <label htmlFor="book-description" className="text-xs font-medium text-app-hint">
            Qisqacha izoh (ixtiyoriy)
          </label>
          <textarea
            id="book-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            disabled={isSubmitting}
            placeholder="Kitob haqida qisqacha..."
            className="rounded-input border border-app-border bg-app-bg px-3 py-2 text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
          />
        </div>

        {errorMessage && (
          <p className="text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Bekor qilish
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            <Save size={16} aria-hidden="true" />
            Saqlash
          </Button>
        </div>
      </div>
    </Modal>
  )
}

interface FieldProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  inputMode?: 'text' | 'numeric'
}

function Field({
  id,
  label,
  value,
  onChange,
  disabled,
  error,
  placeholder,
  inputMode,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-app-hint">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode={inputMode}
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="h-11 w-full rounded-input border border-app-border bg-app-bg px-3 text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
      />
      {error && <p className="text-xs text-stage-rejected-text">{error}</p>}
    </div>
  )
}

function BooksSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-24" />
      ))}
    </div>
  )
}
